'use client'

import { navIconMap } from '@/components/ui/icon-map'
import { cn } from '@/lib/utils/utils'
import { useMounted } from '@/lib/utils/utils-client'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { MdClose, MdOutlineMenu } from 'react-icons/md'
import { getLocalizedUri, I18nValue, LangCode, useI18nValue } from '../i18n/lang-context'
import { LangMenuFlat } from '../i18n/lang-toggle'
import { NavItem } from '../layout/nav-item'
import { Button } from '../ui/button'
import { Flex, FlexCol } from '../ui/flex'
import { LBAction } from './lb/lb-action'
import { LogoHorizontal } from './logo'

export function Menu({ navItems = [], langCode }: { navItems?: NavItem[], langCode: LangCode }) {

  const i18nValue = useI18nValue()
  const [shown, setShown] = useState(false)

  useEffect(() => {
    document.body.style.overflow = shown ? 'hidden' : 'unset'
  }, [shown])

  const mounted = useMounted()
  const pathname = usePathname()

  const getTitle = (title: string | I18nValue) => !title || typeof title === 'string' ? title : i18nValue(title, langCode)

  if (!mounted) return null

  const MenuLinks = ({ title, items, sub }: { title?: string | I18nValue, items: NavItem[], sub?: boolean }) => {
    return (
      <FlexCol
        className={cn(
          'w-full gap-3 px-6',
          sub ? 'pb-2 pt-1' : 'pt-6'
        )}
        onClick={() => setShown(false)}
      >
        {title && (
          <div className={cn(
            'w-full pb-2 pt-6 text-left text-lg font-semibold uppercase tracking-wide opacity-60',
            title !== navItems?.[0]?.title && 'mt-6 border-t border-border'
          )}
          >
            {typeof title === 'string' ? title : i18nValue(title, langCode)}
          </div>
        )}
        {items?.map((item: NavItem, index: number) => item.href ? (
          <Link key={`_${index}`}
                href={getLocalizedUri(item.href, langCode)}
                onClick={() => {if(item.scrollBefore) window.scrollTo(0, 0)}}
                className='w-full'>
            <Button variant='link'
              aria-label='MENU_LINK_BUTTON'
              className={cn(
                'flex w-full items-center justify-start gap-2 px-0 py-2 text-left text-[16px] font-medium transition-all duration-100 active:scale-[0.98]',
                pathname === getLocalizedUri(item.href, langCode)
                  ? ' bg-accent/10 rounded-md font-bold'
                  : 'text-primary dark:text-primaryBright'
              )}
            >
              {item.iconName && navIconMap[item.iconName] && (
                <span className='h-4 w-4 opacity-80'>{navIconMap[item.iconName]}</span>
              )}
              {getTitle(item.title)}
            </Button>
          </Link>
        ) : item.subs && (
          <MenuLinks key={`__${index}`} title={item.title} items={item.subs} sub />
      ))}
      </FlexCol>
    )
  }

  return (
    <>
      <MdOutlineMenu size={32} className='text-primary dark:text-primaryBright' onClick={() => setShown(s => !s)} />
      <div className='fixed right-0 top-0 z-40 flex h-safe-screen w-full flex-col overflow-y-auto bg-background transition-all' style={{
        transform: 'translateX(' + (shown ? '0' : '100%') + ')'
      }}>
        <Flex className='h-16 items-center justify-between p-4'>
          <LogoHorizontal bj={true} onClick={() => setShown(false)} langCode={langCode} />
          <MdClose size={32} className='text-primary dark:text-primaryBright' onClick={() => setShown(false)} />
        </Flex>
        <MenuLinks items={navItems} />
        <div className='w-full px-4 pt-6 text-base'>
          <LBAction className='w-full' langCode={langCode} />
        </div>
        <LangMenuFlat className={'py-6'} themeToggle={true} langCode={langCode}/>
      </div>
    </>
  )
}
